import { useEffect, useState } from 'react';
import { Box, Tabs, Tab, RadioGroup, FormControlLabel, Radio, TextField, Button, Tooltip, Typography, IconButton, Accordion, AccordionSummary, AccordionDetails, Card, CardContent, Chip, CircularProgress, } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useForm, Controller } from 'react-hook-form';
import Header from 'components/Header';
import { useSelector } from 'react-redux';
import { save as saveEvaluation } from 'store/evaluation/admin-eval-rating';
import { useDispatch } from 'react-redux';
import { BasePathProvider, useBasePath } from 'components/Providers';
import Confirm from 'components/Dialog';
import { useTranslation } from 'react-i18next';

const standards = [
    { id: 1, label: 'Standard 1' },
    { id: 2, label: 'Standard 2' },
    { id: 3, label: 'Standard 3' },
    { id: 4, label: 'Standard 4' },
    { id: 5, label: 'Standard 5' },
    { id: 6, label: 'Standard 6' },
];

export default function EvaluateForm() {
    const [currentTab, setCurrentTab] = useState(0);
    const dispatch = useDispatch();
    const basePath = useBasePath();
    const [confirm, setConfirm] = useState(false);
    const [pendingTab, setPendingTab] = useState(null);
    const selfEvaluationList = useSelector(state => state.adminEvaluation.selfEvaluation.list);
    const { t } = useTranslation();

    const artifactList = useSelector(state =>
        state.adminEvaluation.artifact.list.filter(artifact => {
            const standardIds = artifact.indicatorIds.map(indicator => parseInt(indicator.split('.')[0], 10));
            return standardIds.includes(standards[currentTab].id);
        }).map(artifact => ({
            ...artifact,
            indicatorIds: artifact.indicatorIds.filter(indicator => {
                const standardId = parseInt(indicator.split('.')[0], 10);
                return standardId === standards[currentTab].id;
            }).map(indicator => `${standards[currentTab].id}.${indicator.split('.')[1]}`)
        }))
    );

    const merged = standards.map(std => {
        const match = selfEvaluationList?.find(r => r.id === std.id);
        return {
            ...std,
            selfEvaluation: match ? match.adminEvaluationRatings : [],
        };
    });

    const activeEval = merged[currentTab].selfEvaluation[0] || null;

    useEffect(() => {
        if (activeEval) {
            reset({
                rubricId: activeEval.rubricId,
                comment: activeEval.comment,
            });
        } else {
            reset({
                rubricId: '',
                comment: '',
            });
        }
    }, [currentTab, activeEval]);

    const { control, handleSubmit, reset, formState: { isDirty, isSubmitting } } = useForm({
        defaultValues: {
            rubricId: activeEval?.rubricId || '',
            comment: activeEval?.comment || '',
        },
    });

    const adminStandards = useSelector(state => state.lookup.adminStandards?.filter(item => item.type === 1));


    const onSubmit = async (data) => {
        const payload = {
            comment: data.comment,
            rubricId: parseInt(data.rubricId),
            ...(activeEval?.id ? { id: activeEval.id } : { standardId: standards[currentTab].id }),
        };

        await dispatch(saveEvaluation(payload));

    };

    const handleTabChange = (_, newIdx) => {
        if (newIdx === currentTab) return;

        if (isDirty) {
            setPendingTab(newIdx);
            setConfirm(true);
        } else {
            setCurrentTab(newIdx);
        }
    };

    const handleConfirm = () => {
        if (pendingTab !== null) {
            setCurrentTab(pendingTab);
            setPendingTab(null);
        }
        setConfirm(false);
    };


    return (
        <BasePathProvider value={`${basePath}/evaluation`}>
            <Header title={'Evaluation'} small close borderBottom />
            <Tabs value={currentTab} onChange={handleTabChange} sx={{ borderBottom: 1, borderColor: 'divider', pt: 1 }}>
                {standards.map((s) => (
                    <Tab key={s.id} label={s.label} />
                ))}
            </Tabs>

            <Box mt={1} p={2}>
                <Typography variant="body1" fontWeight={500}>{`Effectiveness in ${adminStandards[currentTab]?.name}`} </Typography>
                <Controller
                    name="rubricId"
                    control={control}
                    render={({ field }) => (
                        <RadioGroup {...field}>
                            {adminStandards[currentTab]?.adminEvaluationRubrics?.map((item) => (
                                <FormControlLabel
                                    key={item.score}
                                    value={item.id}
                                    control={<Radio />}
                                    label={
                                        <Box display="flex" alignItems="center">
                                            <Typography>{item.performance}</Typography>
                                            <Tooltip title={item.description || ''} placement='right' arrow >
                                                <IconButton size="small" sx={{ ml: 0.5 }}>
                                                    <InfoOutlinedIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                    }
                                />
                            ))}
                        </RadioGroup>
                    )}
                />

                <Box mt={2}>
                    <Controller
                        name="comment"
                        control={control}
                        render={({ field }) => (
                            <TextField
                                {...field}
                                label="Comments"
                                multiline
                                minRows={3}
                                fullWidth
                            />
                        )}
                    />
                </Box>

                <Box mt={3}>
                    <Accordion>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography>Artifact List</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Box display="flex" flexDirection={'column'} gap={1}>
                                {artifactList.map((art) => (
                                    <Card key={art.id} variant="outlined">
                                        <CardContent>
                                            <Box display="flex" flexDirection="row" flextWrap='wrap' justifyContent="flex-end" mb={2}>
                                                {art.file ?
                                                    <Chip label={'View Attachment'} href={art?.file?.location} component="a" clickable target="_blank" rel="noopener noreferrer" size="small" /> :
                                                    <Chip label={'No Attachment'} color="warning" size="small" />
                                                }
                                            </Box>
                                            <Typography variant="body1">{art?.explanation}</Typography>
                                            <Box display="flex" flexDirection="row" flextWrap='wrap' justifyContent="flex-end" gap={1} mt={2}>
                                                {art?.indicatorIds.map((indicator, index) => (
                                                    <Chip key={index} label={indicator} size="small" />
                                                ))}
                                            </Box>
                                        </CardContent>
                                    </Card>
                                ))}
                            </Box>
                        </AccordionDetails>
                    </Accordion>
                </Box>

                <Box mt={3} textAlign="right">
                    <Button
                        variant="contained"
                        onClick={handleSubmit(onSubmit)}
                        disabled={isSubmitting || !isDirty}
                        startIcon={isSubmitting && <CircularProgress size={20} />}
                    >
                        {t("Save")}
                    </Button>
                </Box>
            </Box>
            <Confirm open={confirm} onClose={() => setConfirm(false)} onConfirm={handleConfirm} message="You have unsaved changes. Are you sure you want to discard changes?" />
        </BasePathProvider>
    );
}
